import { createContext, useContext, useState } from 'react';
import { DashboardFilters } from '../../../components/serviceProvider';
import { AuthConsumer, User } from '../../shared/auth/AuthContext';
import {
  Card,
  DashboardChart,
  Error,
  Flex,
  Loader,
  LottieWrapper,
  TitleBar,
  WrappedQuery,
  NoContentPage,
  EmbeddedExternalDashboard,
} from '../../../components/shared';
import { GET_DEFAULT_SERVICE_PROVIDER_LAYOUT, ALL_USER_INSIGHTS_PAGED } from '../../../graphql';
import { PageInner } from '../../../layouts';
import { ConfigInterface, isUndefined, validateAccountAndPortal } from '../../../utils';
import { Online } from '../../../hooks/useOnlineStatus';
import { OperationVariables, QueryResult } from '@apollo/client';

type ContextType = {
  subjectIds?: string[];
  programmeIds?: string[];
  interventionIds?: string[];
  gradeIds?: string[];
  startDate?: Date;
  endDate?: Date;
};

const initialContext: ContextType = {
  subjectIds: undefined,
  programmeIds: undefined,
  interventionIds: undefined,
  gradeIds: undefined,
  startDate: undefined,
  endDate: undefined,
};

export const DashboardFilterContext = createContext(initialContext);

const Dashboard = () => {
  const contextData = useContext(DashboardFilterContext);
  const [subjectIds, setSubjects] = useState<string[] | undefined>(undefined);
  const [programmeIds, setProgrammes] = useState<string[] | undefined>(undefined);
  const [interventionIds, setInterventions] = useState<string[] | undefined>(undefined);
  const [gradeIds, setGrades] = useState<string[] | undefined>(undefined);
  let [startDate, setStartDate] = useState<Date | undefined>(undefined);
  let [endDate, setEndDate] = useState<Date | undefined>(undefined);
  // const [embed, setEmbed] = useState<{ id: string; html: string; } | null>(null);

  // useEffect(() => {
  //   if (embed === null) return;
  //   const el = document.createElement('div');
  //   el.innerHTML = embed.html;
  //   const cont = document.getElementById(embed.id);
  //   if (!!cont) cont.append(el);
  // }, [embed]);

  const input = Object.values(contextData).every(isUndefined)
    ? {}
    : {
        ...contextData,
        gradeIds: contextData.gradeIds && contextData.gradeIds.length > 0 ? contextData.gradeIds : undefined,
      };

  return (
    <Online>
      <AuthConsumer>
        {({ user: currentUser }: { user: User }) => {
          const id = currentUser.id;
          const { isAdmin } = validateAccountAndPortal(currentUser);

          return (
            <WrappedQuery
              query={ALL_USER_INSIGHTS_PAGED}
              variables={{
                input: {
                  first: 2,
                  ...(!isAdmin && { userId: id }),
                  currentUserId: id,
                },
              }}
              overrideStates={true}
              fetchPolicy="no-cache"
            >
              {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
                if (loading) {
                  return <div>Loading...</div>;
                }
                if (error) {
                  return <Error />;
                }

                if (data && data.insights && data.insights.edges && data.insights.edges.length > 0) {
                  let insightToShow = data.insights.edges[0];

                  const favorite = data.insights.edges.find(({ node }: any) => node.isFavorite);
                  if (favorite) {
                    insightToShow = favorite;
                  }

                  return <EmbeddedExternalDashboard data={insightToShow.node.url} />;
                } else {
                  return (
                    <WrappedQuery query={GET_DEFAULT_SERVICE_PROVIDER_LAYOUT} overrideStates={true}>
                      {({ loading, error, data }: QueryResult<any, OperationVariables>) => {
                        if (error) {
                          return <Error />;
                        }

                        if (loading) {
                          return (
                            <PageInner>
                              <Loader />
                            </PageInner>
                          );
                        }

                        if (data && !data.layout) {
                          return <NoContentPage />;
                        }

                        const json: ConfigInterface = data.layout.config;

                        return (
                          <DashboardFilterContext.Provider
                            value={{ startDate, endDate, subjectIds, programmeIds, interventionIds, gradeIds }}
                          >
                            <PageInner>
                              <DashboardFilters
                                gradeIds={gradeIds}
                                programmeIds={programmeIds}
                                setStartDate={setStartDate}
                                setEndDate={setEndDate}
                                setSubjects={setSubjects}
                                setInterventions={setInterventions}
                                setGrades={setGrades}
                                setProgrammes={setProgrammes}
                              />
                              {json &&
                                json.rows.length &&
                                json.rows.map((row, rowIndex) => {
                                  return (
                                    <Flex size="1" direction="row" key={rowIndex}>
                                      {row.sections.length
                                        ? row.sections.map((section, sectionIndex) => {
                                            return (
                                              <Flex
                                                size={`${section.sectionSize}`}
                                                direction="column"
                                                marginRight="10px"
                                                key={`${rowIndex}_${sectionIndex}`}
                                              >
                                                <TitleBar>{section.sectionTitle || ''}</TitleBar>
                                                <Flex size="1" direction="row">
                                                  {section.columns.length
                                                    ? section.columns.map((column, columnIndex) => {
                                                        return (
                                                          <Flex
                                                            size={`${column.columnSize}`}
                                                            direction="column"
                                                            key={`${rowIndex}_${sectionIndex}_${columnIndex}`}
                                                          >
                                                            {column.cards.map((card, cardIndex) => {
                                                              return (
                                                                <Card
                                                                  m={10}
                                                                  key={`${rowIndex}_${sectionIndex}_${columnIndex}_${cardIndex}`}
                                                                >
                                                                  <p>{card.cardTitle || ''}</p>
                                                                  <Flex size="1" direction="row">
                                                                    {card.charts.length
                                                                      ? card.charts.map((chart, chartIndex) => {
                                                                          const { variables, ...rest } = chart;
                                                                          return (
                                                                            <div
                                                                              style={{
                                                                                width: `${100 / card.charts.length}%`,
                                                                              }}
                                                                              key={`${rowIndex}_${sectionIndex}_${columnIndex}_${cardIndex}_${chartIndex}`}
                                                                            >
                                                                              <DashboardChart
                                                                                {...rest}
                                                                                variables={JSON.stringify({ input })}
                                                                              />
                                                                            </div>
                                                                          );
                                                                        })
                                                                      : null}
                                                                  </Flex>
                                                                </Card>
                                                              );
                                                            })}
                                                          </Flex>
                                                        );
                                                      })
                                                    : null}
                                                </Flex>
                                              </Flex>
                                            );
                                          })
                                        : null}
                                    </Flex>
                                  );
                                })}
                              {(!json || !json.rows.length) && (
                                <div style={{ textAlign: 'center' }}>
                                  <LottieWrapper
                                    width={400}
                                    height={'auto'}
                                    loop={true}
                                    anim={require('../../../assets/lottie/me_at_office.json')}
                                  />
                                  <span>There is no default dashboard currently configured for this portal</span>
                                </div>
                              )}
                            </PageInner>
                          </DashboardFilterContext.Provider>
                        );
                      }}
                    </WrappedQuery>
                  );
                }
              }}
            </WrappedQuery>
          );
        }}
      </AuthConsumer>
    </Online>
  );
};

export default Dashboard;
